import { useEffect } from "react";
import { guideSteps, nextScreen, prevScreen } from "./ablauf.ts";
import { useDraft, useStore } from "./hooks.ts";
import { Kopf } from "./ui/Kopf.tsx";
import { RechtLinks } from "./ui/Recht.tsx";
import { Willkommen } from "./schritte/Willkommen.tsx";
import { Angaben } from "./schritte/Angaben.tsx";
import { Adresse } from "./schritte/Adresse.tsx";
import { Raeume } from "./schritte/Raeume.tsx";
import { Eingang } from "./schritte/Eingang.tsx";
import { Grundriss } from "./schritte/Grundriss.tsx";
import { Fotos } from "./schritte/Fotos.tsx";
import { Markieren } from "./schritte/Markieren.tsx";
import { Leistungen } from "./schritte/Leistungen.tsx";
import { Besonderes } from "./schritte/Besonderes.tsx";
import { Kueche } from "./schritte/Kueche.tsx";
import { Pruefung } from "./schritte/Pruefung.tsx";
import { Danke } from "./schritte/Danke.tsx";
import type { Nav } from "./zustand.ts";

/** Navigation zwischen den Bildschirmen. "Weiter" führt nach "Beheben" zurück zur Prüfung. */
export function useNav() {
  const store = useStore();
  const d = useDraft();
  return {
    go: (nav: Partial<Nav>) => store.go(nav),
    weiter: () =>
      d.nav.returnToCheck
        ? store.go({ screen: "pruefung", returnToCheck: false })
        : store.go({ screen: nextScreen(d.nav.screen, d.rooms), guide: 0, phase: "rundum" }),
    zurueck: () => {
      const screen = prevScreen(d.nav.screen, d.rooms);
      // Von "Besonderes" zurück in den letzten Raum der Fotoaufnahme.
      const guide = screen === "fotos" ? Math.max(0, guideSteps(d.rooms).length - 1) : 0;
      store.go({ screen, guide, phase: "schrank", returnToCheck: false });
    },
  };
}

export function Erfassung() {
  const d = useDraft();
  const { screen, guide, phase } = d.nav;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen, guide, phase]);

  let body;
  switch (screen) {
    case "willkommen":
      body = <Willkommen />;
      break;
    case "kontakt":
    case "termin": // "termin" nur für ältere gespeicherte Stände
      body = <Angaben />;
      break;
    case "auszug":
      body = <Adresse kind="from" />;
      break;
    case "einzug":
      body = <Adresse kind="to" />;
      break;
    case "eingang":
      body = <Eingang />;
      break;
    case "grundriss":
      body = <Grundriss />;
      break;
    case "raeume":
      body = <Raeume />;
      break;
    case "fotos":
      body = <Fotos />;
      break;
    case "markieren":
      body = <Markieren />;
      break;
    case "besonderes":
    case "sonderteile": // ältere gespeicherte Stände (bis 30.10.2026 eigene Seiten)
    case "zerbrechliches":
      body = <Besonderes />;
      break;
    case "kueche":
      body = <Kueche />;
      break;
    case "leistungen":
    case "lampen": // ältere gespeicherte Stände: Lampen stehen seit 30.10.2026 bei den Leistungen
      body = <Leistungen />;
      break;
    case "pruefung":
      body = <Pruefung />;
      break;
    case "danke":
      body = <Danke />;
      break;
  }

  return (
    <div className="app">
      <Kopf />
      {/* key: Jeder Bildschirm startet mit frischem Zustand (Auszug und Einzug sind dieselbe Komponente). */}
      <main className="main" key={screen}>
        {body}
        <RechtLinks privat={d.company.privat} />
      </main>
    </div>
  );
}
